import { useKiosk } from "application/contexts/kiosk/KioskContext";
import Toolbar from "areas/_catalog/components/toolbar/Toolbar";
import {
	StepIndicator,
	CashlessOfflineRechargeCardLayout,
	ApproximateCardLayout,
	RechargeCardLayout,
	CpfStepLayout,
} from "./components";
import { IOrderProviderRef } from "modules/order/presentation/OrderProvider";
import { useTranslation } from "i18n/useTranslation";
import React, { useCallback } from "react";
import { TransactionType } from "domains/order/agreggates/payment/transactionType";
import { OfflineRechargeStep } from "./IUseOffilineRecharge.type";
import { useOfflineRecharge } from "./useOfflineRecharge";
import styles from "../offlineRecharge/OfflineRecharge.module.scss";

/**
 * Recarga Cashless Offline V2 – 3 etapas:
 * 1 – Leitura do cartão (automática)
 * 2 – Valor + pagamento (abertura ou recarga)
 * 3 – Gravação no cartão (com até 3 tentativas)
 */

interface IOfflineRechargePageProps {
	orderProvider: React.RefObject<IOrderProviderRef>;
}

export const OfflineRechargePage: React.FC<IOfflineRechargePageProps> = ({
	orderProvider,
}) => {
	const { kiosk } = useKiosk();
	const { t } = useTranslation();

	const {
		step,
		offlineCard,
		rechargeValueCents,
		activationDisplayValue,
		showRetryReadButton,
		currentStepIndex,
		handleGoBack,
		onRetryRead,
		onSubmitCpf,
		onSubmitRechargeValue,
		addQuickValue,
		handleKeyPress,
		onClearValue,
		getDocumentDisplay,
		resetFlow,
	} = useOfflineRecharge({
		kiosk: kiosk ?? undefined,
		orderProvider,
	});

	const handlePaymentSelect = useCallback(
		(transactionType: TransactionType) => {
			if (!offlineCard?.cardData) return;
			onSubmitRechargeValue({
				uid: offlineCard.uid,
				rechargeValueCents,
				cardData: offlineCard.cardData,
				isClosed: offlineCard.isClosed ?? false,
				transactionType,
			});
		},
		[offlineCard, rechargeValueCents, onSubmitRechargeValue]
	);

	return (
		<div id={styles.OfflineRecharge}>
			<Toolbar
				hideWelcome
				title={kiosk?.localName ?? ""}
				handleGoBack={handleGoBack}
				hideCloseButton={
					step === OfflineRechargeStep.WAITING_CARD_WRITE ||
					step === OfflineRechargeStep.WRITING ||
					step === OfflineRechargeStep.PENDING_RECHARGE ||
					step === OfflineRechargeStep.PENDING_OPEN_ACCOUNT
				}
			/>
			<div className={styles.stepIndicator}>
				<StepIndicator currentStepIndex={currentStepIndex} />
			</div>
			<div className={styles.stepContentArea}>
				{step === OfflineRechargeStep.WAITING_CARD_READ && (
					<CashlessOfflineRechargeCardLayout
						showRetryButton={showRetryReadButton}
						onRetry={onRetryRead}
						line1={t("offline_recharge_v2.steps.waiting_card_read")}
						line2={t("offline_recharge.reading.wait")}
					/>
				)}

				{step === OfflineRechargeStep.WAITING_CUSTOMER_DATA && (
					<CpfStepLayout
						onPressContinuar={(cpfOrCnpj) => onSubmitCpf(cpfOrCnpj ?? null)}
						required={false}
						onPressCancel={handleGoBack}
					/>
				)}

				{step === OfflineRechargeStep.INSERT_RECHARGE_VALUE && offlineCard?.cardData && (
					<RechargeCardLayout
						cardData={offlineCard.cardData}
						isClosed={offlineCard.isClosed ?? false}
						rechargeValueCents={rechargeValueCents}
						activationDisplayValue={activationDisplayValue}
						documentDisplay={getDocumentDisplay(offlineCard.cardData)}
						kiosk={kiosk}
						onAddQuickValue={addQuickValue}
						onKeyPress={handleKeyPress}
						onClearValue={onClearValue}
						onPaymentSelect={handlePaymentSelect}
					/>
				)}

				{step === OfflineRechargeStep.PROCESSING_PAYMENT && (
					<ApproximateCardLayout
						line1={t("offline_recharge_v2.steps.processing_payment")}
						line2={t("offline_recharge.reading.wait")}
					/>
				)}

				{step === OfflineRechargeStep.WRITING && (
					<ApproximateCardLayout
						line1={t("offline_recharge_v2.writing_approximate_reader")}
						line2={t("offline_recharge_v2.writing_do_not_remove_until_done")}
					/>
				)}

				{step === OfflineRechargeStep.ERROR && (
					<ApproximateCardLayout
						line1={t("offline_recharge_v2.error_read_title")}
						line2={t("offline_recharge_v2.error_read_message")}
						showRetryButton
						onRetry={resetFlow}
					/>
				)}
				{step === OfflineRechargeStep.PENDING_RECHARGE && (
					<ApproximateCardLayout
						line1={t("offline_recharge_v2.steps.pending_recharge")}
						line2={t("offline_recharge_v2.steps.pending_recharge_do_not_remove")}
					/>
				)}
				{step === OfflineRechargeStep.PENDING_OPEN_ACCOUNT && (
					<ApproximateCardLayout
						line1={t("offline_recharge_v2.steps.pending_recharge")}
						line2={t("offline_recharge_v2.steps.pending_recharge_do_not_remove")}
					/>
				)}
			</div>
		</div>
	);
};

export default OfflineRechargePage;
